/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
import { useEffect, useCallback, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import Form from '../../../components/forms/Form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import '../../../assets/styles/custom-styles.css'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import {
  transformFormDataForApi,
  cleanPayload,
  normalizeFormErrors,
} from '../../../utils/objectUtils'
import PageHeader from '../../../components/PageHeader'
import SectionHeader from '../../../components/forms/SectionHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import MovementEnum from '../../../enums/movement-enum'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { Alert, Box, Typography } from '@mui/material'
import ItemListSection from '../../../components/forms/ItemListSection'
import { useOrderServicePageLogic } from './hook/useOrderServicePageLogic'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'

const EditOrderServicePage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { serviceOrderId } = useParams()
  // Cria um objeto URLSearchParams para pegar os parâmetros da query
  const queryParams = new URLSearchParams(location.search)

  const contractId = queryParams.get('contract_id')

  const {
    put: baseServicePut,
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
  } = useBaseService(navigate)

  const getMostFrequent = (items, key) => {
    if (!items || items.length === 0) return null

    const counts = items.reduce((acc, item) => {
      const val = item[key]
      if (val) {
        // Usamos o objeto como string para a chave do contador, mas guardamos a referência
        const keyStr = JSON.stringify(val)
        acc[keyStr] = acc[keyStr] || { count: 0, data: val }
        acc[keyStr].count += 1
      }
      return acc
    }, {})

    // Retorna o item que teve a maior contagem
    return Object.values(counts).sort((a, b) => b.count - a.count)[0]?.data
  }

  const { data: contractOsDetails } = useQueryWithLoader({
    queryKey: ['contractOsDetails', contractId, serviceOrderId],
    queryFn: async () => {
      if (!contractId && serviceOrderId) {
        const response = await baseServiceGetByColumn(
          entities.serviceOrders.getById(serviceOrderId)
        )
        return response.result
      } else if (contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.contracts.contractServiceOrder(contractId, serviceOrderId)
        )
        return response.result
      }
    },
    enabled: !!contractId || !!serviceOrderId,
    select: (data) => {
      const frequentAddress = getMostFrequent(data.items, 'address')
      return {
        order: {
          id: {
            value: data.id,
            label: data.id,
          },
          movement_type_id: {
            value: data.movement_type?.id,
            label: data.movement_type?.name,
          },
          contract_id: data.contract?.id
            ? {
                value: data.contract?.id,
                label: `${data.contract?.number} - ${data.contract?.name}`,
              }
            : null,
          recipient_customer_id: {
            value: data.recipient_customer?.id,
            label: `${data.recipient_customer?.id} - ${data.recipient_customer?.alias}`,
          },
          // destination_order_id: {
          //   value: data.destination_order?.id,
          //   label: data.destination_order?.name || "-",
          // },
          address_id: frequentAddress
            ? {
                value: frequentAddress.id,
                label: `${frequentAddress.id} - ${frequentAddress.formatted_address}`,
              }
            : null,
          location_id: data?.items?.[0]?.location
            ? {
                value: data?.items?.[0]?.location?.id,
                label: `${data.items?.[0]?.location?.id} - ${data.items?.[0]?.location?.formatted_location}`,
              }
            : null,
          mode_transport_id: {
            value: data.mode_transport?.id,
            label: data.mode_transport?.name,
          },
          deadline: data.deadline?.split(' ')[0],
          details: data.details,
          requires_invoice: {
            value: data.requires_invoice ? 1 : 0,
            label: data.requires_invoice ? 'Sim' : 'Não',
          }
        },
      }
    },
  })

  const { data: osEquipments } = useQueryWithLoader({
    queryKey: ['contractOsItemsEquipments', contractId, serviceOrderId],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        type: 'equipment',
        deleted_at: false,
      })
      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 9999999999,
        sort: {
          column: 'id',
          order: 'asc',
        },
      }

      if (!contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.serviceOrders.items(serviceOrderId),
          params
        )
        return response.result.data
      } else if (contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.contracts.contractServiceOrderItems(
            contractId,
            serviceOrderId
          ),
          params
        )
        return response.result.data
      }
    },
    enabled: !!contractId || !!serviceOrderId,
    select: (data) => ({
      equipments: data.map((item) => {
        return {
          id: item.id,
          service_order_item_id: item.id,
          movement_type_id: {
            value: item.movement_type?.id,
            label: item.movement_type?.name,
          },
          contract_item_id: {
            id: item?.contract_item?.id,
            value: item?.contract_item?.id,
            label: `${item?.contract_item?.alias} - ${item?.contract_item?.description} (Total: ${item?.contract_item?.quantity}, Restante: ${item.available_quantity_current ?? 0})`,
          },
          root_item_id: item?.contract_item?.root_item_id,
          attended_quantity: item?.contract_item?.attended_quantity,
          available_quantity: item?.contract_item?.available_quantity,
          equipment_id: item.equipment_id
            ? {
                value: item.equipment_id,
                label: `${item.equipment?.number} - ${item.equipment?.name}`,
              }
            : null,
          quantity: item.quantity,
          available_quantity_current: item.available_quantity_current ?? null,
          quantity_in_other_requests: item.quantity_in_other_requests ?? null,
          department_id: {
            value: item.department?.id,
            label: item.department?.name,
          },
          type_id: item.type?.id
            ? {
                value: item.type?.id,
                label: item.type?.name,
              }
            : null,
          address_id: item.address?.id
            ? {
                value: item.address?.id,
                label: item.address?.formatted_address,
              }
            : null,
          location_id: item.location?.id
            ? {
                value: item.location?.id,
                label: item.location?.formatted_location,
              }
            : null,
          details: item.details,
          is_exchange: Boolean(item.is_exchange),
          is_extra: Boolean(item.is_extra),
          extras_list: (item.extra_equipments || []).map((extra) => {
            return {
              id: extra.id,
              quantity: extra.pivot?.quantity,
              equipment_id: {
                value: item?.contract_item?.id,
                label: `${item?.contract_item?.alias} - ${item?.contract_item?.description}`,
              },
              type_id: extra.id
                ? {
                    value: extra.id,
                    label: extra.name,
                  }
                : null,
              details: extra.details,
            }
          }),
        }
      }),
    }),
  })

  const { data: osProducts } = useQueryWithLoader({
    queryKey: ['contractOsItemsProducts', contractId, serviceOrderId],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        type: 'product',
        deleted_at: false,
      })
      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 99999999,
      }

      if (!contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.serviceOrders.items(serviceOrderId),
          params
        )
        return response.result.data
      } else if (contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.contracts.contractServiceOrderItems(
            contractId,
            serviceOrderId
          ),
          params
        )
        return response.result.data
      }
    },
    enabled: !!contractId || !!serviceOrderId,
    select: (data) => ({
      products: data.map((item) => ({
        id: item.id,
        service_order_item_id: item.id,
        movement_type_id: {
          value: item.movement_type?.id,
          label: item.movement_type?.name,
        },
        product_id: {
          value: item.product?.id,
          label: item.product?.name,
        },
        quantity_product: item.quantity_product,
        department_id: {
          value: item.department?.id,
          label: item.department?.name,
        },
        details: item.details,
        address_id: item.address?.id
          ? {
              value: item.address?.id,
              label: item.address?.formatted_address,
            }
          : null,
        location_id: item.location?.id
          ? {
              value: item.location?.id,
              label: item.location?.formatted_location,
            }
          : null,
        _original: item,
      })),
    }),
  })

  const equipmentsData = osEquipments?.equipments ?? []

  const extraEquipmentsData = equipmentsData
    .flatMap((eq) => eq.extras_list || [])
    .map((extra) => ({
      id: extra.value || extra.id || crypto.randomUUID(),
      equipment_id: extra.equipment_id,
      type_id: extra.type_id,
      quantity: extra.quantity,
    }))

  const productsData = osProducts?.products ?? []

  const [contractAmount, setContractAmount] = useState(null)

  const { form, order, equipments, items, extras } = useOrderServicePageLogic({
    isEditPage: true,
    originalData: {
      equipmentsData,
      extraEquipmentsData,
      productsData,
    },
    contractAmount,
  })

  const { data: contractAmountResponse } = useQueryWithLoader({
    queryKey: ['contractAmountDetails', form.contract?.id],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.contractAmount(form.contract?.id),
        {
          deleted_at: false,
        }
      )
      return response.result
    },
    enabled: !!form.contract?.id,
    staleTime: 10 * 60 * 1000,
    refetchOnMount: true,
  })

  useEffect(() => {
    setContractAmount(contractAmountResponse)
  }, [contractAmountResponse])

  useEffect(() => {
    // Se os dados ainda não carregaram, não faz nada
    if (!contractOsDetails || !osEquipments || !osProducts) return

    // 🛡️ PROTEÇÃO: Verifica se existe pelo menos um equipamento antes de tentar acessar suas propriedades
    const firstEquipment = osEquipments.equipments?.[0]

    order.setFormData((prev) => ({
      ...prev,
      order: {
        ...prev.order,
        ...contractOsDetails.order,
        // Só aplica o fallback se o address_id da ordem for null E existir um primeiro equipamento
        ...(contractOsDetails.order?.address_id === null &&
          firstEquipment && {
            address_id: firstEquipment.address_id,
            location_id: firstEquipment.location_id,
          }),
      },
    }))

    // 🛡️ PROTEÇÃO: Garante que sempre seja um array, mesmo que vazio
    equipments.setData(osEquipments.equipments || [])
    equipments.setFormData(osEquipments.equipments || [])
    equipments.setEquipments(osEquipments.equipments || [])

    items.setData(osProducts.products || [])
    items.setFormData(osProducts.products || [])
    items.setItems(osProducts.products || [])

    const allExtras = (osEquipments.equipments || [])
      .flatMap((eq) => eq.extras_list || [])
      .map((extra) => ({
        id: extra.value || extra.id || crypto.randomUUID(),
        equipment_id: extra.equipment_id,
        type_id: extra.type_id,
        quantity: extra.quantity,
      }))

    extras.setData(allExtras)
    extras.setFormData(allExtras)
    extras.setExtras(allExtras)

    order.setFormData((prev) => ({
      ...prev,
      equipments: osEquipments.equipments || [],
      items: osProducts.products || [],
      extras: allExtras,
    }))
  }, [contractOsDetails, osEquipments, osProducts])

  // Mutações TanStack Query
  const EditContractOsMutation = useMutation({
    mutationFn: async (dataToEdit) => {
      form.setErrors({})

      const response = await baseServicePut(
        entities.serviceOrders.update(serviceOrderId),
        dataToEdit
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        order.resetOrderServiceForm()
        navigate(-1, {
          state: { type: 'success' },
        })

        // Invalida OrdersServicePage
        queryClient.invalidateQueries({ queryKey: ['orders'] })

        // Invalida CreateOrderServicePage
        queryClient.invalidateQueries({ queryKey: ['contractOsDetails'] })
        queryClient.invalidateQueries({
          queryKey: ['contractOsItemsEquipments'],
        })
        queryClient.invalidateQueries({
          queryKey: ['contractOsItemsProducts'],
        })
        queryClient.invalidateQueries({ queryKey: ['contractAmountDetails'] })

        // Invalida ContractOsPage
        queryClient.invalidateQueries({ queryKey: ['contractOrders'] })

        // Invalida CreateMovementPage
        queryClient.invalidateQueries({ queryKey: ['ordersServices'] })
        queryClient.invalidateQueries({ queryKey: ['serviceOrderItems'] })
        queryClient.invalidateQueries({
          queryKey: ['contractItemsForMovement'],
        })
      }
    },
    onError: (err) => {
      console.error('onError ', err)
      if (err.response?.data?.errors) {
        form.setErrors(err.response.data.errors)
      }
    },
  })

    const handleSubmit = useCallback(async () => {
      try {
        const formDataToApi = {
      ...order.formData,

      // Fonte real das listas do ItemListSection
      equipments: equipments.data ?? [],
      items: items.data ?? [],
      extras: extras.data ?? [],
    }

    console.log('========== DADOS ANTES DA TRANSFORMAÇÃO ==========')
    console.log('equipments.data:', equipments.data)
    console.log('items.data:', items.data)
    console.log('extras.data:', extras.data)
    console.log('===================================================')

    const transformedFormData =
      transformFormDataForApi(formDataToApi)

    const cleanedPayload = cleanPayload(transformedFormData)

    console.log('========== DADOS APÓS TRANSFORMAÇÃO ==========')
    console.log('equipments:', cleanedPayload.equipments)
    console.log('items:', cleanedPayload.items)
    console.log('extras:', cleanedPayload.extras)
    console.log('===============================================')

    // 🆕 HELPER: extrai o ID primitivo de objetos {value, label} ou retorna o valor direto
    const getPrimitiveId = (val) => {
      if (val === null || val === undefined) return null
      if (typeof val === 'object') return val.value ?? val.id ?? null
      return val
    }

    // 🛡️ 1. Extrair o movement_type_id da ordem de forma segura (fallback em cascata)
    const orderMovementTypeId =
      order.selects.movementType.selectedValue?.value ||
      getPrimitiveId(cleanedPayload.order?.movement_type_id)

    // 🛡️ 2. Extrair os IDs de endereço de forma segura
    const safeAddressId = getPrimitiveId(cleanedPayload.order?.address_id)
    const safeLocationId = getPrimitiveId(cleanedPayload.order?.location_id)

    // 🛡️ 3. Montar o payload forçando os campos críticos
    const payload = {
      ...cleanedPayload,

      order: {
        ...cleanedPayload.order,
        organization_id: form.orgao?.id,
        requires_invoice: Boolean(
          Number(getPrimitiveId(cleanedPayload.order?.requires_invoice) ?? 0)
        ),
      },

      equipments: (cleanedPayload?.equipments ?? []).map(
        (equipment) => {
          const eqId = String(
            getPrimitiveId(equipment.id)
          )

          const eqContractItemId = String(
            getPrimitiveId(equipment.contract_item_id)
          )

          const relatedExtras = (
            cleanedPayload?.extras ?? []
          )
            .filter((extra) => {
              const extraEqId = String(
                getPrimitiveId(extra.equipment_id)
              )

              return (
                extraEqId === eqContractItemId ||
                extraEqId === eqId
              )
            })
            .map((extra) => ({
              type_id: Number(
                getPrimitiveId(extra.type_id)
              ),
              quantity: Number(extra.quantity),
            }))

          const {
            service_order_item_id,
            ...restEquipment
          } = equipment

          const soItemId =
            getPrimitiveId(service_order_item_id)

          return {
            ...restEquipment,

            ...(soItemId != null && {
              service_order_item_id: soItemId,
            }),

            movement_type_id:
              Number(orderMovementTypeId) || null,

            is_exchange:
              Boolean(equipment.is_exchange),

            // NÃO depende mais de order.formData.extras
            is_extra:
              relatedExtras.length > 0,

            // Extras efetivamente associados ao equipamento
            extras: relatedExtras,

            address_id:
              safeAddressId ||
              getPrimitiveId(equipment.address_id),

            location_id:
              safeLocationId ||
              getPrimitiveId(equipment.location_id),
          }
        }
      ),

      items: (cleanedPayload?.items ?? []).map(
        (item) => {
          const {
            service_order_item_id,
            ...restItem
          } = item

          const soItemId =
            getPrimitiveId(service_order_item_id)

          return {
            ...restItem,

            ...(soItemId != null && {
              service_order_item_id: soItemId,
            }),

            movement_type_id:
              Number(orderMovementTypeId) || null,

            address_id:
              safeAddressId ||
              getPrimitiveId(item.address_id),

            location_id:
              safeLocationId ||
              getPrimitiveId(item.location_id),
          }
        }
      ),
    }

    console.log('🚀 Payload Final Blindado:', payload)
    console.log(
      '🔎 EXTRAS POR EQUIPAMENTO:',
      payload.equipments.map((e) => ({
        service_order_item_id: e.service_order_item_id ?? null,
        is_extra: e.is_extra,
        extras: e.extras,
      }))
    )

    await EditContractOsMutation.mutateAsync(payload)
  } catch (error) {
    console.error('Erro ao enviar requisição:', {
      status: error.status,
      message: error.message,
      errors: error.response?.data?.errors,
    })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  EditContractOsMutation,
  equipments.data,
  extras.data,
  items.data,
  order.formData,
  order.selects.movementType.selectedValue?.value,
  form.orgao?.id,
])

  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos/${form.contract?.id}/requisicoes/`)
  }, [navigate, form.contract?.id])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Editar Requisição`}
        showBackButton={true}
        backUrl={`/aluguel/contratos/${form.contract?.id}/requisicoes`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Salvar"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={EditContractOsMutation.isPending}>
          {() => (
            <>
              <div key={order.section}>
                <SectionHeader section={order.section} />
                <SimpleBody
                  fields={order.fieldsState}
                  formErrors={normalizeFormErrors(form.formErrors)}
                  formData={order.formData}
                  handleFieldChange={order.handleChange}
                  getOptions={order.getOptions}
                  getSelectedValue={order.getSelectedValue}
                  getSelectMeta={order.getSelectMeta}
                />
              </div>
              <Box
                key={equipments.section}
                sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                {/* EQUIPAMENTOS */}
                <ItemListSection
                  section={equipments.section}
                  fields={equipments.fieldsState}
                  initialData={equipments.data}
                  onItemsChange={equipments.onItemsChange}
                  renderFormBody={equipments.renderFormBody}
                  getItemDisplayValue={equipments.getItemDisplayValue}
                  itemFormData={equipments.formData}
                  onItemChange={equipments.handleChange}
                  itemFormErrors={form.formErrors}
                  onFormSubmit={equipments.onFormSubmit}
                  onDeleteItem={equipments.onDeleteItem}
                  initializeItemForm={equipments.initializeItemForm}
                  isDisabledButton={form.disabledButton}
                  getOptions={equipments.getOptions}
                  getSelectedValue={equipments.getSelectedValue}
                  getSelectMeta={equipments.getSelectMeta}
                  captureAction={equipments.captureAction}
                  notShowHeader={[
                    'equipments.location_id',
                    'equipments.details',
                  ]}
                  headerCustom={() => {
                    if (
                      order.selects.movementType.selectedValue?.value !==
                      MovementEnum.ALUGUEL
                    )
                      return null
                    return (
                      // Aparecer somente quando o tipo de movimento for aluguel
                      <>
                        <br />
                        {!order.hasBalance && (
                          <Alert severity="warning">
                            <Typography sx={{ fontSize: '1rem' }}>
                              O contrato não possui mais saldo disponível, então
                              ao adicionar um item sem saldo, ele será
                              considerado como excedente de contrato. Será
                              necessário uma autorização da gerência para essa
                              requisição
                            </Typography>
                          </Alert>
                        )}
                        {/* Separator */}
                        <hr />
                      </>
                    )
                  }}
                />
              </Box>

              {/* Somente para aluguel */}
              {order.selects.movementType.selectedValue?.value ===
                MovementEnum.ALUGUEL && (
                <>
                  <Box
                    key={extras.section}
                    sx={{
                      mt: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {/* KIT DE EQUIPAMENTOS */}
                    <ItemListSection
                      section={extras.section}
                      fields={extras.fields}
                      initialData={extras.data}
                      onItemsChange={extras.onItemsChange}
                      renderFormBody={extras.renderFormBody}
                      getItemDisplayValue={extras.getItemDisplayValue}
                      itemFormData={extras.formData}
                      onItemChange={extras.handleChange}
                      itemFormErrors={form.formErrors}
                      onFormSubmit={extras.onFormSubmit}
                      initializeItemForm={extras.initializeItemForm}
                      isDisabledButton={form.disabledButton}
                      getOptions={extras.getOptions}
                      getSelectedValue={extras.getSelectedValue}
                      getSelectMeta={extras.getSelectMeta}
                    />
                  </Box>
                </>
              )}

              {/* ITEMS */}
              {[
                MovementEnum.ALUGUEL,
                MovementEnum.RESERVA,
                MovementEnum.AVULSO,
              ].includes(order.selects.movementType.selectedValue?.value) && (
                <>
                  <Box
                    key={items.section}
                    sx={{
                      mt: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {/* ITEMS */}
                    <ItemListSection
                      section={items.section}
                      fields={items.fields}
                      initialData={items.data}
                      onItemsChange={items.onItemsChange}
                      renderFormBody={items.renderFormBody}
                      getItemDisplayValue={items.getItemDisplayValue}
                      itemFormData={items.formData}
                      onItemChange={items.handleChange}
                      itemFormErrors={form.formErrors}
                      onFormSubmit={items.onFormSubmit}
                      initializeItemForm={items.initializeItemForm}
                      isDisabledButton={form.disabledButton}
                      getOptions={items.getOptions}
                      getSelectedValue={items.getSelectedValue}
                      getSelectMeta={items.getSelectMeta}
                      captureAction={items.captureAction}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default EditOrderServicePage
